import {
  Alert,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  Modal,
  Textarea,
  ThemeIcon,
} from '@mantine/core';
import { CircleBackButton } from '../../components/common/CircleBackButton';
import { FullContentHeader } from '../../components/layout/FullContentHeader';
import { SessionDescriptionBlock } from '../../components/common/SessionDescriptionBlock';
import { ParticipantLabelDivergenceIndicator } from '../../components/common';
import { composeCoachSessionLabel } from './utils/sessionLabel';
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  Check,
  Clock,
} from 'react-feather';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  coachSessionPrepApi,
  type AiPromptSegmentSelections,
  type AiPromptSelectionsDoc,
  type CoachPrepPayload,
  type PrepChecklistState,
} from '../../services/api/coachSessionPrep';
import type {
  ComposeEnvelope,
  PrepSuggestionsOutput,
  PreSessionGuidanceOutput,
} from '../../services/api/aiTypes';
import { useFeatureFlag } from '../../stores/featureFlags';
import { useSlideDeckDoc } from '../../components/common/slides/useSlideDeckDoc';
import { SessionAgendaAccordion } from '../cohorts/components/sessions/SessionAgendaAccordion';
import { AuthoredSessionAgendaAccordion } from '../cohorts/components/sessions/AuthoredSessionAgendaAccordion';
import { MarkAsReviewedControl } from '../cohorts/components/sessions/MarkAsReviewedControl';
import { ViewPrepSheetButton } from '../cohorts/components/sessions/ViewPrepSheetButton';
import { PreviewButton } from '../cohorts/components/sessions/PreviewButton';
import { CoachPrepGroupSection } from '../cohorts/components/sessions/CoachPrepGroupSection';

/**
 * MED-793 / Story 2.5a — Coach Session Prep page.
 *
 * Route: /coach/cohorts/:cohortId/sessions/:sessionId/prep
 *
 * Top-level layout (matches screenshot 1):
 *   • Page header: back button + title + sub-info (cohort · org · duration)
 *   • Upper section: Session Agenda accordion (coach mode) — Leader Notes
 *     per step, Mark As Reviewed / View Prep Sheet / Preview header actions.
 *   • Lower section: CoachPrepGroupSection — one tab per assigned group,
 *     status row + roster + "Before you start" checklist.
 *
 * AC5 — first-time strong-prompt banner shown above the agenda when
 *       `is_first_time_coach` is true. Dismissible per session.
 * AC6 — if the server marks the session as live (status='in_progress')
 *       we display a friendly notice and link back to the coach cohort.
 * AC2 — RSVP confirmation lives in the page header as an inline summary
 *       with an action menu (Accept / Tentative / Decline with reason).
 */

// MED-1126 — presence heartbeat cadence. Matches the participant waiting room's
// POLL_INTERVAL_MS (4s) so the coach's last_seen_at stays comfortably inside the
// server's 10s "present" window while they prep, making the coach show as present
// to participants waiting in the room.
const PRESENCE_HEARTBEAT_INTERVAL_MS = 4_000;

export interface CoachSessionPrepPageProps {
  /**
   * MED-1089 — DEV-only static preview: seeds the prep payload and bypasses the
   * feature-flag gate + fetch so the page renders from an in-memory fixture
   * (`/dev/session-prep/coach`). Mutating handlers become optimistic local-only
   * updates in preview (no network). Compiled out of production builds.
   */
  preview?: {
    payload: CoachPrepPayload;
    /**
     * MED-1098 — optional pre-seeded AI suggestions envelope for the dev preview,
     * threaded to the agenda's per-step AI Suggestions sections so the prompt
     * cards + AI note render without the AI backend.
     */
    aiEnvelope?: ComposeEnvelope<PrepSuggestionsOutput>;
    /**
     * MED-1100 — optional pre-seeded sentiment/guidance envelope for the dev
     * preview, threaded to the group section's Sentiment & Guidance panel.
     */
    sentimentEnvelope?: ComposeEnvelope<PreSessionGuidanceOutput>;
  };
}

export function CoachSessionPrepPage({
  preview,
}: CoachSessionPrepPageProps = {}) {
  const isPreview = !!preview;
  const { cohortId, sessionId } = useParams<{
    cohortId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  // MED-890 / AC2: Gate the prep flow page behind the live_session_delivery
  // flag. useFeatureFlag fails closed (returns false while loading), satisfying
  // the "or while flag state is still loading" clause.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const [payload, setPayload] = useState<CoachPrepPayload | null>(
    preview?.payload ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [markSaving, setMarkSaving] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await coachSessionPrepApi.getPrep(sessionId);
      setPayload(fresh);
      setErrorMsg(null);
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 410) {
        setErrorMsg(
          'This session is already live — the prep view is no longer accessible.'
        );
      } else if (status === 403) {
        setErrorMsg('You do not have access to this prep flow.');
      } else if (status === 404) {
        setErrorMsg('Session not found.');
      } else {
        setErrorMsg('Could not load prep. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    // Preview seeds the payload directly — no fetch, no flag gate.
    if (isPreview) return;
    setLoading(true);
    void refresh();
  }, [refresh, isPreview]);

  // MED-1126 — presence heartbeat. Coaches never enter the participant waiting
  // room (they prep here, then Start), so without this their session_attendance
  // last_seen_at goes stale and participants see the coach as "not yet here".
  // Pinging a dedicated lightweight endpoint (NOT a prep refetch — that would
  // clobber unsaved leader notes) keeps the coach present in the room. Gated on
  // the LSD flag since the waiting room only exists behind it. Fire-and-forget:
  // a failed heartbeat is non-blocking and self-heals on the next tick.
  useEffect(() => {
    if (isPreview || !sessionId || !liveSessionDeliveryEnabled) return;
    const ping = () => {
      void coachSessionPrepApi.heartbeat(sessionId).catch(() => {});
    };
    ping();
    const intervalId = window.setInterval(ping, PRESENCE_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [sessionId, isPreview, liveSessionDeliveryEnabled]);

  // -- AC11 Mark As Reviewed handlers --
  const handleMark = useCallback(async () => {
    if (isPreview) {
      setPayload(prev =>
        prev
          ? {
              ...prev,
              prep: {
                ...prev.prep,
                marked_reviewed_at: new Date().toISOString(),
              },
            }
          : prev
      );
      return;
    }
    if (!sessionId) return;
    setMarkSaving(true);
    try {
      await coachSessionPrepApi.markReviewed(sessionId);
      await refresh();
    } catch {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: 'Try again in a moment.',
      });
    } finally {
      setMarkSaving(false);
    }
  }, [sessionId, refresh, isPreview]);

  const handleClear = useCallback(async () => {
    if (isPreview) {
      setPayload(prev =>
        prev
          ? { ...prev, prep: { ...prev.prep, marked_reviewed_at: null } }
          : prev
      );
      return;
    }
    if (!sessionId) return;
    setMarkSaving(true);
    try {
      await coachSessionPrepApi.clearReviewed(sessionId);
      await refresh();
    } catch {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: 'Try again in a moment.',
      });
    } finally {
      setMarkSaving(false);
    }
  }, [sessionId, refresh, isPreview]);

  // -- AC9 leader-notes save (MED-1081: autosaved) --
  // The Leader Notes editor now autosaves on a debounce. Merge the saved note
  // into local state optimistically instead of a full refresh() + success toast
  // on every keystroke-batch — a refetch would refetch all prep data (and risk
  // clobbering in-progress text), and a toast per save would spam the user.
  // Errors propagate so the editor's inline "Save failed" indicator surfaces.
  const handleSaveNote = useCallback(
    async (args: {
      blockKey: string;
      scope: 'all_groups' | 'group';
      groupId: string | null;
      contentHtml: string;
    }) => {
      if (isPreview) {
        setPayload(prev => {
          if (!prev) return prev;
          const without = prev.leader_notes.filter(
            n =>
              !(
                n.block_key === args.blockKey &&
                n.scope === args.scope &&
                (n.group_id ?? null) === (args.groupId ?? null)
              )
          );
          const empty = !args.contentHtml || args.contentHtml.trim() === '';
          if (empty) return { ...prev, leader_notes: without };
          const now = new Date().toISOString();
          const note = {
            id: `preview-note-${args.blockKey}-${args.scope}-${args.groupId ?? 'all'}`,
            coach_user_id: 'preview-coach',
            session_id: prev.session.id,
            block_key: args.blockKey,
            scope: args.scope,
            group_id: args.groupId,
            content_html: args.contentHtml,
            content_plain: args.contentHtml.replace(/<[^>]+>/g, ''),
            created_at: now,
            updated_at: now,
            is_deleted: false,
          };
          return { ...prev, leader_notes: [...without, note] };
        });
        return;
      }
      if (!sessionId) return;
      const saved = await coachSessionPrepApi.saveLeaderNote(sessionId, {
        block_key: args.blockKey,
        scope: args.scope,
        group_id: args.groupId,
        content_html: args.contentHtml,
      });
      setPayload(prev => {
        if (!prev) return prev;
        const without = prev.leader_notes.filter(
          n =>
            !(
              n.block_key === args.blockKey &&
              n.scope === args.scope &&
              (n.group_id ?? null) === (args.groupId ?? null)
            )
        );
        // saved === null means the note was soft-deleted (empty content).
        return { ...prev, leader_notes: saved ? [...without, saved] : without };
      });
    },
    [sessionId, isPreview]
  );

  // -- AC2 RSVP handlers --
  const handleRsvp = useCallback(
    async (status: 'accepted' | 'declined' | 'tentative', reason?: string) => {
      if (isPreview) {
        setPayload(prev =>
          prev
            ? {
                ...prev,
                prep: {
                  ...prev.prep,
                  rsvp_status: status,
                  rsvp_responded_at: new Date().toISOString(),
                  rsvp_decline_reason:
                    status === 'declined' ? (reason ?? null) : null,
                },
              }
            : prev
        );
        setDeclineModalOpen(false);
        setDeclineReason('');
        return;
      }
      if (!sessionId) return;
      try {
        await coachSessionPrepApi.setRsvp(sessionId, {
          status,
          decline_reason: status === 'declined' ? reason : undefined,
        });
        await refresh();
        setDeclineModalOpen(false);
        setDeclineReason('');
        notifications.show({
          color: status === 'declined' ? 'yellow' : 'medleyGreen',
          title: 'RSVP recorded',
          message:
            status === 'declined'
              ? 'Your decline has been noted. Reassignment will follow up.'
              : 'Thanks for confirming.',
        });
      } catch {
        notifications.show({
          color: 'red',
          title: 'Save failed',
          message: 'Try again in a moment.',
        });
      }
    },
    [sessionId, refresh, isPreview]
  );

  const handlePatchChecklist = useCallback(
    async (patch: PrepChecklistState) => {
      if (isPreview) {
        setPayload(prev =>
          prev
            ? {
                ...prev,
                prep: {
                  ...prev.prep,
                  checklist_state: { ...prev.prep.checklist_state, ...patch },
                },
              }
            : prev
        );
        return;
      }
      if (!sessionId) return;
      try {
        await coachSessionPrepApi.patchChecklist(sessionId, patch);
        await refresh();
      } catch {
        notifications.show({
          color: 'red',
          title: 'Save failed',
          message: 'Try again in a moment.',
        });
      }
    },
    [sessionId, refresh, isPreview]
  );

  // ── MED-876 AC2 / IV3 — In Use selections persistence ───────────────────
  // Local optimistic state mirrors payload.prep.ai_prompt_selections so the
  // AI Suggestions sections (per step) update instantly when the coach toggles
  // a slot; the PATCH then confirms (or reverts on failure).
  const [aiSelections, setAiSelections] = useState<AiPromptSelectionsDoc>({});
  useEffect(() => {
    if (payload?.prep.ai_prompt_selections) {
      setAiSelections(payload.prep.ai_prompt_selections);
    }
  }, [payload?.prep.ai_prompt_selections]);

  const handleSaveAiSelections = useCallback(
    async (
      segmentKey: string,
      next: AiPromptSegmentSelections
    ): Promise<void> => {
      if (!sessionId && !isPreview) return;
      const previous = aiSelections;
      const merged: AiPromptSelectionsDoc = { ...previous, [segmentKey]: next };
      setAiSelections(merged); // optimistic
      if (isPreview || !sessionId) return; // preview: keep the optimistic state, no network
      try {
        const result = await coachSessionPrepApi.patchAiSelections(
          sessionId,
          merged
        );
        setAiSelections(result.ai_prompt_selections);
      } catch {
        setAiSelections(previous); // revert
        notifications.show({
          color: 'red',
          title: 'Could not save selection',
          message: 'Try again in a moment.',
        });
      }
    },
    [sessionId, aiSelections, isPreview]
  );

  // ── MED-1079 — load the slide-deck PDF once for thumbnail rendering ───────
  // Enabled only when an authored section actually has associated slides, so
  // sessions without visuals never fetch the deck. The loaded doc is threaded
  // into the agenda accordion, where each section renders its slides as
  // thumbnails (falling back to page-number chips while loading / on failure).
  const hasSlideVisuals =
    payload?.agenda.mode === 'authored' &&
    payload.agenda.authored.sections.some(s => s.visuals.length > 0);
  const { doc: slideDeckDoc } = useSlideDeckDoc({
    cohortId,
    sessionId,
    enabled: Boolean(hasSlideVisuals),
  });

  // ── render guards ───────────────────────────────────────────────────────

  // MED-890 / AC2: Redirect to coach home when live_session_delivery is off
  // (or still loading). Placed after all hook calls to respect Rules of Hooks.
  // The dev preview seeds the payload directly and bypasses this gate.
  // MED-1181: also redirect when the cohort's per-modality LSD toggle is off on
  // the loaded payload (defense-in-depth). This single gate removes the roster
  // reflection column, the checklist, and the Start/Rejoin buttons at once for
  // a classic MED01 cohort. Null payload (still loading) does not trip it.
  if (
    !isPreview &&
    (!liveSessionDeliveryEnabled ||
      payload?.session.live_session_delivery_enabled === false)
  ) {
    return <Navigate to="/coach" replace />;
  }

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (errorMsg || !payload) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <CircleBackButton
            onClick={() =>
              navigate(
                cohortId
                  ? `/coach/cohorts/${cohortId}`
                  : '/coach/cohort-dashboard'
              )
            }
          />
          <Alert
            color="yellow"
            icon={<AlertTriangle size={16} />}
            title="Cannot open prep"
          >
            {errorMsg ?? 'Unknown error.'}
          </Alert>
        </Stack>
      </Container>
    );
  }

  // MED-952 AC5 — the session being live no longer dead-ends the prep page with
  // a blocking alert. The page renders normally and each in-progress group
  // surfaces a "Rejoin Session" affordance in its GroupStatusRow (below), so a
  // coach who left can get back into the live session. (The old AC6 blocking
  // alert was the exact lockout this story removes.)

  const backToCohort = () =>
    navigate(
      cohortId ? `/coach/cohorts/${cohortId}` : '/coach/cohort-dashboard'
    );

  return (
    <>
      {/* MED-1089 (AC1) — shared FullContentHeader replaces the page's bespoke
          header strip: back nav + session title + program · client · duration.
          The RSVP summary lives in the header accessory slot (right of the
          title). */}
      <FullContentHeader
        data-testid="coach-prep-header"
        title={
          // MED-1085 identity ("Session N: {Topic}") via the shared coach label
          // rule (same shape as the dashboard/cohort-detail surfaces, MED-1106),
          // plus the MED-1147 internal-awareness indicator when participants see
          // a different label than this canonical topic.
          <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
            <Text fz={24} fw={700} c="#1A1A1A" lh={1.1}>
              {composeCoachSessionLabel({
                session_number: payload.session.session_number,
                topic_name: payload.session.session_topic,
                title: payload.session.title,
              })}
            </Text>
            <ParticipantLabelDivergenceIndicator
              differs={payload.session.participant_display_differs}
              participantDisplayName={payload.session.topic_display_name}
              size={16}
            />
          </Group>
        }
        programName={payload.session.cohort_name}
        clientName={payload.session.organization_name}
        durationMinutes={payload.session.duration_minutes}
        onBack={backToCohort}
        sticky
        accessoryPlacement="band"
        accessory={
          <RsvpInlineSummary
            status={payload.prep.rsvp_status}
            onAccept={() => handleRsvp('accepted')}
            onTentative={() => handleRsvp('tentative')}
            onOpenDecline={() => setDeclineModalOpen(true)}
          />
        }
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          {/* AC1 — locked notice (rare; server allows fetch but flags unlock state) */}
          {!payload.session.prep_unlocked && (
            <Alert
              color="gray"
              icon={<Clock size={16} />}
              title="Prep not yet unlocked"
            >
              Prep for this session unlocks once the previous session has been
              completed.
            </Alert>
          )}

          {/* AC5 — first-time strong-prompt banner (page-top, dismissible per session) */}
          {payload.is_first_time_coach && !bannerDismissed && (
            <Alert
              color="medleyGreen"
              variant="light"
              withCloseButton
              onClose={() => setBannerDismissed(true)}
              icon={<Check size={16} />}
              title="Before your first session"
            >
              Coaches who complete the full prep sequence consistently report
              stronger sessions. Walk the agenda, add Leader Notes on the
              moments you want to land, mark RSVPs, and click{' '}
              <strong>Mark As Reviewed</strong> when you're ready.
            </Alert>
          )}

          {/* MED-1101 (AC2/AC3/AC4) — Session Description at the top of the
            content area, below the page banners (locked notice + first-time
            prompt) and above the Session Agenda. Hidden entirely when unset
            (AC9). Source: session_number_config.description (AC7). */}
          <SessionDescriptionBlock description={payload.description} />

          {/* MED-976 (E1.3h) — Upper section: Session Agenda.
            Discriminates on agenda.mode (`live_session_delivery` flag gate).

            mode='authored' is the new e1.3d/e/f path:
              - AC-CP1: if is_incomplete=true → "Session content is still being prepared" Alert
              - else render AuthoredSessionAgendaAccordion (authored sections + Leader Prompts
                + Participant Prompt read-only)

            mode='parsed_outline' is the legacy path (flag OFF). The page itself redirects
            on flag OFF, but the union is preserved for type safety + race-condition robustness. */}
          {payload.agenda.mode === 'authored' ? (
            payload.agenda.authored.is_incomplete ? (
              <Alert
                color="gray"
                icon={<CalendarIcon size={16} />}
                title="Prep not yet ready"
              >
                Session content is still being prepared — prep unlocks once it's
                complete.
              </Alert>
            ) : (
              <AuthoredSessionAgendaAccordion
                sections={payload.agenda.authored.sections}
                slideDeckDoc={slideDeckDoc}
                coachContext={{
                  groups: payload.groups,
                  leaderNotes: payload.leader_notes,
                  onSaveLeaderNote: handleSaveNote,
                  startCollapsed: Boolean(payload.prep.marked_reviewed_at),
                  sessionId: payload.session.id,
                  aiFeaturesEnabled: payload.ai_features_enabled,
                  aiPromptSelections: aiSelections,
                  onSaveAiSelections: handleSaveAiSelections,
                  previewAiEnvelope: preview?.aiEnvelope,
                  headerActionsSlot: (
                    <>
                      <PreviewButton
                        cohortId={cohortId}
                        sessionId={payload.session.id}
                      />
                      <ViewPrepSheetButton sessionId={payload.session.id} />
                      <MarkAsReviewedControl
                        markedAt={payload.prep.marked_reviewed_at}
                        saving={markSaving}
                        onMark={handleMark}
                        onClear={handleClear}
                      />
                    </>
                  ),
                }}
              />
            )
          ) : payload.agenda.parsed_outline ? (
            <SessionAgendaAccordion
              outline={payload.agenda.parsed_outline}
              renderConfig={{
                mode: 'coach',
                coachContext: {
                  groups: payload.groups,
                  leaderNotes: payload.leader_notes,
                  onSaveLeaderNote: handleSaveNote,
                  startCollapsed: Boolean(payload.prep.marked_reviewed_at),
                  sessionId: payload.session.id,
                  aiFeaturesEnabled: payload.ai_features_enabled,
                  aiPromptSelections: aiSelections,
                  onSaveAiSelections: handleSaveAiSelections,
                  headerActionsSlot: (
                    <>
                      <PreviewButton
                        cohortId={cohortId}
                        sessionId={payload.session.id}
                      />
                      <ViewPrepSheetButton sessionId={payload.session.id} />
                      <MarkAsReviewedControl
                        markedAt={payload.prep.marked_reviewed_at}
                        saving={markSaving}
                        onMark={handleMark}
                        onClear={handleClear}
                      />
                    </>
                  ),
                },
              }}
            />
          ) : (
            <Alert
              color="gray"
              icon={<CalendarIcon size={16} />}
              title="Agenda not available"
            >
              {payload.agenda.unavailable_reason === 'still_processing'
                ? 'Session content is still being processed. Refresh in a moment.'
                : payload.agenda.unavailable_reason === 'failed'
                  ? 'Session content failed to process. Reach out to your Cohort Manager.'
                  : 'Your Cohort Manager has not uploaded session content yet.'}
            </Alert>
          )}

          {/* Lower section — Group tabs */}
          <CoachPrepGroupSection
            groups={payload.groups}
            prep={payload.prep}
            onPatchChecklist={handlePatchChecklist}
            cohortId={cohortId ?? ''}
            aiFeaturesEnabled={payload.ai_features_enabled}
            sessionNumber={payload.session.session_number}
            previewSentimentEnvelope={preview?.sentimentEnvelope}
            // MED-1181 / AC4: the roster "Pre-session reflection" column only
            // makes sense when reflection content is actually configured.
            // MED-1182 (E1.3f-R1) — reflection prompts are now optional, so
            // "configured" means ≥1 prompt (the removed below-minimum flag only
            // cleared at 2+, which would have wrongly hidden the column for a
            // valid single-prompt session). Uses the authored `has_reflection_prompts`
            // signal so the coach column and the participant form agree.
            hasReflectionContent={
              payload.agenda.mode === 'authored' &&
              payload.agenda.authored.has_reflection_prompts
            }
          />
        </Stack>

        {/* Decline-reason modal */}
        <Modal
          opened={declineModalOpen}
          onClose={() => setDeclineModalOpen(false)}
          title="Decline this session?"
          centered
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Reassignment will be coordinated by your Cohort Manager. A short
              reason helps the team line up coverage.
            </Text>
            <Textarea
              placeholder="Reason (optional)"
              value={declineReason}
              onChange={e => setDeclineReason(e.currentTarget.value)}
              autosize
              minRows={3}
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setDeclineModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="dangerRed"
                onClick={() => void handleRsvp('declined', declineReason)}
              >
                Confirm decline
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers (local — keep page self-contained)
// ---------------------------------------------------------------------------

function RsvpInlineSummary({
  status,
  onAccept,
  onTentative,
  onOpenDecline,
}: {
  status: 'pending' | 'accepted' | 'declined' | 'tentative';
  onAccept: () => void;
  onTentative: () => void;
  onOpenDecline: () => void;
}) {
  const config = {
    pending: { label: 'RSVP needed', color: 'yellow' },
    accepted: { label: '✓ Attending', color: 'medleyGreen' },
    declined: { label: 'Declined', color: 'dangerRed' },
    tentative: { label: 'Tentative', color: 'gray' },
  }[status];

  return (
    <Group gap="xs">
      <ThemeIcon variant="light" color={config.color} size="md" radius="xl">
        <Check size={12} />
      </ThemeIcon>
      <Text size="sm" fw={500}>
        {config.label}
      </Text>
      <Menu position="bottom-end" shadow="md">
        <Menu.Target>
          <Button variant="subtle" size="xs">
            Change
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={onAccept}>I'll be there</Menu.Item>
          <Menu.Item onClick={onTentative}>Tentative</Menu.Item>
          <Menu.Item onClick={onOpenDecline} color="dangerRed">
            I can't make it
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

export default CoachSessionPrepPage;
